import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import api from '@/api'

interface UserProfile {
  permissions?: unknown
}

interface UseUserPermissionsOptions {
  enabled?: boolean
}

const normalizePermissions = (value: unknown): Set<string> => {
  if (!Array.isArray(value)) {
    return new Set()
  }

  return new Set(
    value
      .filter((item): item is string => typeof item === 'string')
      .map((item) => item.trim())
      .filter(Boolean),
  )
}

/**
 * Fresh permission keys of the signed-in account.
 * Permissions stay unknown while the profile request is pending or failed, so callers
 * must treat `hasPermission()` as false until the account is resolved.
 */
export const useUserPermissions = ({ enabled = true }: UseUserPermissionsOptions = {}) => {
  const { data, isSuccess, isPending, isError, error, refetch } = useQuery({
    queryKey: ['user', 'profile'],
    queryFn: () => api.get<UserProfile>('/user/profile', { suppressErrorToast: true }),
    enabled,
    retry: false,
    staleTime: 5 * 60 * 1000,
  })

  const permissions = computed(() =>
    isSuccess.value ? normalizePermissions(data.value?.permissions) : null,
  )

  const hasPermission = (permissionKey: string) => permissions.value?.has(permissionKey) ?? false
  const permissionsReady = computed(() => isSuccess.value)

  return { hasPermission, permissionsReady, isPending, isError, error, refetch }
}
